/**
 * Settings page "Mein Profil" (`/settings/profile`): the logged-in user changes their own first name,
 * last name and e-mail address. A new e-mail address is confirmed with the current password; the field
 * for it appears only then.
 *
 * Loads `GET /api/v1/me/profile` (which gives the uid) and the change-tracked `User` proxy for that uid
 * from `UserRepository`. The form is built with @ha/formhandler (`own-profile-form.ts`, bound to the
 * `ha-input-text` fields with `bindTo`). On save the changed names go onto the proxy and out as the
 * entity command `update.user` (only the changed names, ADR-015); a changed e-mail goes out as the
 * action command `change.own-email-address` with the password. Both together are sent as one bulk, so
 * the server saves both or neither. Every logged-in user may do this for their own record (self
 * policy), so neither the route nor this view checks a privilege. The refusal texts come from
 * `OwnProfileState`.
 *
 * A second section, "Passwort ändern", has a form of its own (`own-password-form.ts`) with its own
 * teardown, saving and error state, so saving one section never touches the other: current password,
 * new password (rule S6) and its repetition go out as the action command `change.own-password`
 * (`saveOwnPassword`). On success every other login of the user ends; this tab keeps its login through
 * the cookie of the response. The typed passwords are cleared after every outcome.
 *
 * A third section, "API-Tokens", is its own element (`ha-own-api-tokens`) with its own loading,
 * saving and error state; it only gets the own uid from here.
 *
 * The fields carry the issue dialog's layout classes (`max9999-flexrow max355-flexcolumn`,
 * theme-dialog-default.css), so label and value sit in one row as there.
 *
 * Extends `AbstractO2dElement` (Light DOM, ADR-007); styles in ralphy.css.
 */

import { AbstractO2dElement } from '@o2d/shared';
import { get } from 'lit-translate';
import { html } from 'lit';
import type { TemplateResult } from 'lit';
import { customElement } from 'lit/decorators/custom-element.js';
import { state } from 'lit/decorators/state.js';
import { container } from 'tsyringe';
import { bindTo } from '@ha/formhandler';
import type { ComplexFormControl, PrimitiveFormControl } from '@ha/formhandler';
import { Subject, takeUntil } from 'rxjs';
import '../components/_inputs/ha-input-text/ha-input-text.component';
import '../components/ha-own-api-tokens/ha-own-api-tokens.component';
import { BackendClient } from '../connectivity/backend-client';
import { CommandBus } from '../services/command-bus.service';
import { GlobalNotificationService } from '../services/global-notification.service';
import { saveOwnProfile } from '../helpers/own-profile-save';
import { saveOwnPassword } from '../helpers/own-password-save';
import type { TrackedUser } from '../helpers/own-profile-save';
import { UserRepository } from '../domain/repositories/user.repository';
import { OwnProfileState } from '../helpers/own-profile.state';
import type { OwnPasswordErrorKey, OwnProfile, OwnProfileErrorKey } from '../helpers/own-profile.state';
import { createOwnProfileForm, isOwnProfileFormSavable, ownProfileNeedsPassword } from '../components/_forms/own-profile-form';
import type { OwnProfileFormData } from '../components/_forms/own-profile-form';
import { createOwnPasswordForm, isOwnPasswordFormSavable, ownPasswordRepeatDiffers, ownPasswordRuleNotMet } from '../components/_forms/own-password-form';
import type { OwnPasswordFormData } from '../components/_forms/own-password-form';
import { PASSWORD_LOCK_MINUTES, PASSWORD_MIN_LENGTH } from '../helpers/password-rule';
import { UiHelper } from '../helpers/ui.helper';
import { NOTIFICATION_DURATION } from '../constants';

@customElement('ha-own-profile-view')
export class HaOwnProfileView extends AbstractO2dElement {

    @state() private _saved: OwnProfile | null = null;

    /** The change-tracked proxy of the logged-in user; `update.user` carries it as its payload. */
    private _user: TrackedUser | null = null;

    @state() private _form: ComplexFormControl<OwnProfileFormData, unknown> | null = null;

    @state() private _saving = false;

    /** Ends the subscription of the current form (a new one replaces it after every save). */
    private readonly _formTeardown$ = new Subject<void>();

    @state() private _error: OwnProfileErrorKey | null = null;

    /** The section "Passwort ändern": its own form, teardown, saving and error state. */
    @state() private _passwordForm: ComplexFormControl<OwnPasswordFormData, unknown> | null = null;

    @state() private _passwordSaving = false;

    @state() private _passwordError: OwnPasswordErrorKey | null = null;

    private readonly _passwordFormTeardown$ = new Subject<void>();

    private _loadStarted = false;

    public disconnectedCallback(): void {
        this._formTeardown$.next();
        this._passwordFormTeardown$.next();
        super.disconnectedCallback();
    }

    public connectedCallback(): void {
        super.connectedCallback();
        if (!this._loadStarted) {
            this._loadStarted = true;
            void this._load();
        }
    }

    private async _load(): Promise<void> {
        try {
            const profile = await container.resolve(BackendClient).get<OwnProfile>('/api/v1/me/profile');
            if (!profile) {
                throw new Error('empty profile response');
            }
            const user = await container.resolve(UserRepository).findOneById(profile.uid);
            if (!user) {
                throw new Error('own user record not found');
            }
            // The repository may hand out a user loaded earlier in the session; start from the profile
            // just read, so the names compare against the stored state and nothing counts as changed.
            const trackedUser = user as TrackedUser;
            trackedUser.firstName = profile.firstName;
            trackedUser.lastName = profile.lastName;
            trackedUser.emailAddress = profile.emailAddress;
            trackedUser.changeTracker.reset();
            this._user = trackedUser;
            this._applySaved(profile);
            this._resetPasswordForm();
        } catch (error) {
            console.error(error);
            container.resolve(GlobalNotificationService).error(get('ownProfile.load.errorTitle'), get('ownProfile.load.errorMessage'));
        }
    }

    /**
     * Takes the stored profile as the new baseline: a fresh form with these values (so the password
     * field is empty again and nothing counts as changed); every change re-renders and clears an
     * earlier refusal text.
     */
    private _applySaved(profile: OwnProfile): void {
        this._formTeardown$.next();
        this._saved = profile;
        const previous = this._form;
        const form = createOwnProfileForm(profile);
        form.event$.pipe(takeUntil(this._formTeardown$)).subscribe(notification => {
            if (notification.event === 'value.changed') {
                this._error = null;
            }
            this.requestUpdate();
        });
        this._form = form;
        previous?.dispose();
    }

    /**
     * A fresh, empty "Passwort ändern" form — after loading and after a successful change, so no typed
     * password stays in a field; every change re-renders and clears an earlier refusal text.
     */
    private _resetPasswordForm(): void {
        this._passwordFormTeardown$.next();
        const previous = this._passwordForm;
        const form = createOwnPasswordForm();
        form.event$.pipe(takeUntil(this._passwordFormTeardown$)).subscribe(notification => {
            if (notification.event === 'value.changed') {
                this._passwordError = null;
            }
            this.requestUpdate();
        });
        this._passwordForm = form;
        previous?.dispose();
    }

    /** Sends `change.own-password` through `saveOwnPassword`; the typed passwords are cleared after every outcome. */
    private readonly _onChangePassword = async (): Promise<void> => {
        const saved = this._saved;
        const form = this._passwordForm;
        if (saved === null || form === null || this._passwordSaving || !isOwnPasswordFormSavable(form)) {
            return;
        }

        this._passwordSaving = true;
        this._passwordError = null;
        try {
            const outcome = await saveOwnPassword(saved.uid, form.getValue()!, container.resolve(CommandBus));
            if (outcome.kind === 'refused') {
                // Clearing the fields emits value.changed, which clears `_passwordError` — so the error
                // is set AFTER it, not before.
                for (const key of ['currentPassword', 'newPassword', 'repeatPassword'] as const) {
                    (form.getField(key) as PrimitiveFormControl<string, unknown>).value = '';
                }
                this._passwordError = OwnProfileState.passwordErrorKey(outcome.error);
                return;
            }

            this._resetPasswordForm();
            UiHelper.notify({
                closable: true,
                level: 'info',
                title: get('ownPassword.save.successTitle'),
                content: html`<p>${get('ownPassword.save.successMessage')}</p>`,
                duration: NOTIFICATION_DURATION,
            });
        } finally {
            this._passwordSaving = false;
        }
    };

    /** The section "Passwort ändern". */
    private _renderPasswordSection(form: ComplexFormControl<OwnPasswordFormData, unknown>): TemplateResult {
        const rule = { minLength: String(PASSWORD_MIN_LENGTH), minutes: String(PASSWORD_LOCK_MINUTES) };

        return html`
            <h3 class="own-profile__title own-password__title">${get('ownPassword.title')}</h3>
            <p class="own-profile__hint">${get('ownPassword.hint', rule)}</p>
            <div class="own-profile__form own-password__form">
                <ha-input-text class="own-password__current max9999-flexrow max355-flexcolumn" data-type="password-current"
                    label=${get('ownPassword.currentPassword')} obscure ${bindTo(form, 'currentPassword')}></ha-input-text>
                <ha-input-text class="own-password__new max9999-flexrow max355-flexcolumn" data-type="password-new"
                    label=${get('ownPassword.newPassword')} obscure ${bindTo(form, 'newPassword')}></ha-input-text>
                <ha-input-text class="own-password__repeat max9999-flexrow max355-flexcolumn" data-type="password-repeat"
                    label=${get('ownPassword.repeatPassword')} obscure ${bindTo(form, 'repeatPassword')}></ha-input-text>
            </div>
            ${ownPasswordRuleNotMet(form) ? html`<p class="own-password__rule" data-type="password-rule">${get('ownPassword.ruleNotMet', rule)}</p>` : html``}
            ${ownPasswordRepeatDiffers(form) ? html`<p class="own-password__rule" data-type="password-repeat-differs">${get('ownPassword.repeatDiffers')}</p>` : html``}
            ${this._passwordError !== null
                ? html`<p class="own-password__error" data-type="password-error" role="alert">${get(`ownPassword.error.${this._passwordError}`, rule)}</p>`
                : html``}
            <button class="own-password__save" type="button"
                ?disabled=${this._passwordSaving || !isOwnPasswordFormSavable(form)}
                @click=${this._onChangePassword}>
                ${get('ownPassword.save.button')}
            </button>
        `;
    }

    /** Saves through `saveOwnProfile` (see there for what is sent and how the proxy is kept clean). */
    private readonly _onSave = async (): Promise<void> => {
        const saved = this._saved;
        const form = this._form;
        const user = this._user;
        if (saved === null || form === null || user === null || this._saving || !isOwnProfileFormSavable(form, saved)) {
            return;
        }

        this._saving = true;
        this._error = null;
        try {
            const outcome = await saveOwnProfile(user, saved, form.getValue()!, container.resolve(CommandBus));
            if (outcome.kind === 'nothing') {
                this._applySaved(saved);
                return;
            }
            if (outcome.kind === 'refused') {
                // The typed password is not kept in the form after a refusal. Clearing it emits
                // value.changed, which clears `_error` — so the error is set AFTER it, not before.
                (form.getField('currentPassword') as PrimitiveFormControl<string, unknown>).value = '';
                this._error = OwnProfileState.errorKey(outcome.error);
                return;
            }

            this._applySaved(outcome.profile);
            UiHelper.notify({
                closable: true,
                level: 'info',
                title: get('ownProfile.save.successTitle'),
                content: html`<p>${get('ownProfile.save.successMessage')}</p>`,
                duration: NOTIFICATION_DURATION,
            });
        } finally {
            this._saving = false;
        }
    };

    protected render(): TemplateResult {
        const saved = this._saved;
        const form = this._form;
        if (saved === null || form === null) {
            return html`<div class="view__body own-profile"><h2 class="own-profile__title">${get('ownProfile.title')}</h2></div>`;
        }

        return html`
            <div class="view__body own-profile">
                <h2 class="own-profile__title">${get('ownProfile.title')}</h2>
                <p class="own-profile__hint">${get('ownProfile.hint')}</p>
                <div class="own-profile__form">
                    <ha-input-text class="own-profile__username max9999-flexrow max355-flexcolumn" data-type="username" label=${get('ownProfile.username')}
                        .value=${saved.username} readonly></ha-input-text>
                    <ha-input-text class="own-profile__first-name max9999-flexrow max355-flexcolumn" data-type="first-name" label=${get('ownProfile.firstName')}
                        ${bindTo(form, 'firstName')}></ha-input-text>
                    <ha-input-text class="own-profile__last-name max9999-flexrow max355-flexcolumn" data-type="last-name" label=${get('ownProfile.lastName')}
                        ${bindTo(form, 'lastName')}></ha-input-text>
                    <ha-input-text class="own-profile__email max9999-flexrow max355-flexcolumn" data-type="email" label=${get('ownProfile.emailAddress')}
                        ${bindTo(form, 'emailAddress')}></ha-input-text>
                    ${ownProfileNeedsPassword(form, saved)
                        ? html`<ha-input-text class="own-profile__current-password max9999-flexrow max355-flexcolumn" data-type="password"
                                label=${get('ownProfile.currentPassword')} obscure
                                ${bindTo(form, 'currentPassword')}></ha-input-text>`
                        : html``}
                </div>
                ${this._error !== null
                    ? html`<p class="own-profile__error" role="alert">${get(`ownProfile.error.${this._error}`, { minutes: String(PASSWORD_LOCK_MINUTES) })}</p>`
                    : html``}
                <button class="own-profile__save" type="button"
                    ?disabled=${this._saving || !isOwnProfileFormSavable(form, saved)}
                    @click=${this._onSave}>
                    ${get('ownProfile.save.button')}
                </button>
                ${this._passwordForm !== null ? this._renderPasswordSection(this._passwordForm) : html``}
                <ha-own-api-tokens class="own-api-tokens" .userUid=${saved.uid}></ha-own-api-tokens>
            </div>
        `;
    }
}
